// src/warehouse/recipes.js — раздел „Рецептури" в склада (ПРОМТ-13).
//
// Рецептурата (калкулационна карта) казва КОЛКО материал отива в една
// порция. Цената не идва оттук: при изписването складът взема средната
// претеглена към датата. Затова себестойността на порция в картата е само
// информационна и е надписана така изрично.
//
// Версиите: карта, използвана в потвърдено изписване, е заключена — промяна
// става с нова версия с „В сила от". Правилата (цикли, заключване, коя
// версия е активна) са на сървъра (routes/recipes.js, services/recipes.js).

import { el, clear, field, input, row, clickableRow } from '../shared/dom.js';
import { combobox } from '../shared/combobox.js';
import { dateInput, isoToBg, localTodayIso } from '../shared/dates.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { loadError, emptyState, searchInput } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { impReadFile } from '../shared/importers.js';
import { state, cq } from '../shared/state.js';
import { guessColumns } from './pos.js';
// Мерките на реда: базовата, преобразуванията на материала и стандартната
// метрична (г към кг) — без „g" и „г" като две различни мерки.
import { unitsOf } from '../shared/units.js';
import { plural, periodLabel, amount, quantity } from '../shared/format.js';

// Отвореното ястие; null = списъкът.
let _dishId = null;
let _q = '';
export function openDish(id) { _dishId = id == null ? null : Number(id); }

const f2 = amount;
const fq = quantity;
const monthStart = () => localTodayIso().slice(0, 8) + '01';
const STATUS = { draft: ['чернова', 'amber'], active: ['активна', 'green'], superseded: ['заменена', 'gray'] };
const statusTag = (s) => { const x = STATUS[s] || [s || '—', 'gray']; return el('span', { class: 'tag ' + x[1], text: x[0] }); };
// Материалите на картата: материал, стока или друго ястие (полуфабрикат).
const LINE_TYPES = ['material', 'goods', 'dish'];

// „v2 от 01.09.2026" — ползва се и в „Артикули".
export function recipeLabel(r) { return r ? 'v' + r.version + ' от ' + isoToBg(r.valid_from) : ''; }
// Активната карта на ястието (най-високата версия със статус „активна").
export function activeRecipe(cards, articleId) {
  return (cards || []).filter((c) => String(c.article_id) === String(articleId) && c.status === 'active')
    .sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0))[0] || null;
}

// Дошли сме от отворен месец в „Продажби от POS" — път обратно към него.
function posBack(ctx) {
  return ctx && ctx.backToPos ? el('button', { class: 'btn sm ghost', type: 'button', text: '← Към продажбите от POS', onClick: ctx.backToPos }) : null;
}

export async function recipesTab(area, ctx) {
  let arts, cards;
  try {
    arts = await api('/articles' + cq());
    cards = await api('/recipes' + cq());
  } catch (e) { area.appendChild(loadError(() => { clear(area); recipesTab(area, ctx); }, e.message)); return; }
  arts = Array.isArray(arts) ? arts : [];
  cards = Array.isArray(cards) ? cards : [];
  if (_dishId != null) {
    const dish = arts.find((a) => Number(a.id) === _dishId);
    if (dish) return dishDetail(area, ctx, dish, arts);
    _dishId = null;
  }
  const dishes = arts.filter((a) => a.type === 'dish' && a.active !== false)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'bg'));
  const panel = el('div', { class: 'panel' });
  panel.appendChild(el('div', { class: 'panel-head' }, el('h2', { text: 'Рецептури' }),
    searchInput({ placeholder: 'Ястие…', value: _q, ariaLabel: 'Търсене на ястие', onSearch: (q) => { _q = q; draw(); } }),
    el('div', { class: 'row-actions' },
      posBack(ctx),
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Внос от Excel', onClick: () => importDialog(ctx) }))));
  panel.appendChild(el('p', { class: 'cal-hint', text: 'Рецептурата казва колко материал отива в една порция. Цената при изписването идва от склада (средна цена), не от картата.' }));
  if (!dishes.length) {
    panel.appendChild(emptyState('☷', 'Няма артикули тип „Ястие". Добавете ястията в „Артикули" с тип Ястие, после въведете рецептурите им тук или ги внесете от Excel.'));
    area.appendChild(panel);
    return;
  }
  const tb = el('tbody');
  const note = el('div', { role: 'status', class: 'cal-hint' });
  function draw() {
    clear(tb);
    const list = dishes.filter((d) => !_q || [d.code, d.name].some((v) => String(v || '').toLowerCase().includes(_q)));
    for (const d of list) {
      const act = activeRecipe(cards, d.id);
      const drafts = cards.filter((c) => String(c.article_id) === String(d.id) && c.status === 'draft').length;
      const st = act
        ? el('span', {}, recipeLabel(act))
        : drafts ? el('span', { class: 'tag amber', text: 'само чернова' }) : el('span', { class: 'tag red', text: 'няма рецептура' });
      tb.appendChild(clickableRow(() => { _dishId = Number(d.id); ctx.reload(); }, 'Рецептура на ' + d.name,
        el('td', { style: 'font-variant-numeric:tabular-nums' }, d.code || ''),
        el('td', { style: 'font-weight:600' }, d.name || ''),
        el('td', {}, st),
        el('td', { class: 'num' }, act ? String((act.lines || []).length) : '—'),
        el('td', { class: 'num' }, act && act.portion_cost != null ? f2(act.portion_cost) : '—')));
    }
    note.textContent = list.length ? '' : 'Нищо не е намерено.';
    note.style.display = list.length ? 'none' : '';
  }
  panel.appendChild(el('table', { class: 'list' },
    el('thead', {}, el('tr', {}, el('th', { text: 'Код' }), el('th', { text: 'Ястие' }), el('th', { text: 'Рецептура' }),
      el('th', { class: 'num', text: 'Материали' }), el('th', { class: 'num', text: 'Себест. на порция (информ.)' }))),
    tb));
  panel.appendChild(note);
  draw();
  area.appendChild(panel);
}

async function dishDetail(area, ctx, dish, arts) {
  const back = el('button', { class: 'btn sm ghost', type: 'button', text: '← Към рецептурите', onClick: () => { _dishId = null; ctx.reload(); } });
  let versions;
  try { versions = await api('/recipes' + cq() + '&article_id=' + encodeURIComponent(dish.id)); }
  catch (e) { area.appendChild(el('div', {}, back)); area.appendChild(loadError(() => { clear(area); dishDetail(area, ctx, dish, arts); }, e.message)); return; }
  versions = (Array.isArray(versions) ? versions : []).slice().sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0));
  const base = activeRecipe(versions, dish.id) || versions[0] || null;
  const panel = el('div', { class: 'panel' });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Рецептура: ' + (dish.code ? dish.code + ' ' : '') + dish.name }),
    el('div', { class: 'row-actions' }, posBack(ctx), back,
      el('button', { class: 'btn sm', type: 'button', text: versions.length ? '+ Нова версия' : '+ Първа версия', onClick: () => recipeForm(ctx, dish, arts, { base }) }))));
  if (!versions.length) {
    panel.appendChild(emptyState('☷', 'Ястието още няма рецептура. Докато я няма, месецът от POS с това ястие не може да се потвърди.', '+ Първа версия', () => recipeForm(ctx, dish, arts, {})));
    area.appendChild(panel);
    return;
  }
  for (const r of versions) panel.appendChild(versionBlock(ctx, dish, arts, r));
  area.appendChild(panel);
}

function versionBlock(ctx, dish, arts, r) {
  const locked = !!r.locked || (Array.isArray(r.used_in) && r.used_in.length > 0);
  const editable = !locked && r.status !== 'superseded';
  // Месецът с думи („Август 2026"), не „2026-08".
  const usedRaw = Array.isArray(r.used_period_labels) && r.used_period_labels.length ? r.used_period_labels : r.used_periods;
  const used = Array.isArray(usedRaw) && usedRaw.length ? usedRaw.map(periodLabel) : null;
  const box = el('section', { class: 'recipe-version', style: 'border-top:1px solid var(--border);padding:12px 0' });
  box.appendChild(el('h3', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin:0 0 6px' },
    'Версия ' + r.version, statusTag(r.status), el('span', { style: 'font-weight:400', text: 'в сила от ' + isoToBg(r.valid_from) })));
  if (locked) box.appendChild(el('p', { class: 'cal-hint', text: 'Използвана в изписване' + (used ? ' за ' + used.join(', ') : '') + ' — не се променя. За промяна направете нова версия.' }));
  if (r.note) box.appendChild(el('p', { style: 'margin:0 0 6px', text: r.note }));
  const portion = Number(r.portion) || 1;
  box.appendChild(el('p', { style: 'margin:0 0 6px' },
    'Себестойност на порция: ', el('strong', { text: r.portion_cost == null ? 'не може да се изчисли' : f2(r.portion_cost) + ' €' }),
    el('span', { class: 'cal-hint', style: 'display:inline;padding:0 0 0 6px', text: '(информационно — по средните цени в склада; при изписването цената идва от склада към датата)' }),
    portion !== 1 ? el('span', { text: ' Количествата са за ' + portion + ' порции.' }) : null));
  box.appendChild(el('table', { class: 'list' },
    el('thead', {}, el('tr', {}, el('th', { text: 'Код' }), el('th', { text: 'Материал' }), el('th', { class: 'num', text: 'Количество' }),
      el('th', { text: 'Единица' }), el('th', { class: 'num', text: 'Средна цена' }), el('th', { class: 'num', text: 'Стойност' }))),
    el('tbody', {}, ...(r.lines || []).map((l) => el('tr', {},
      el('td', {}, l.code || ''), el('td', {}, l.name || ('Артикул ' + l.article_id)), el('td', { class: 'num' }, fq(l.qty)),
      el('td', {}, l.unit || l.measure || ''), el('td', { class: 'num' }, l.avg == null ? '—' : f2(l.avg)),
      el('td', { class: 'num' }, l.cost == null ? '—' : f2(l.cost)))))));
  const acts = el('div', { style: 'display:flex;gap:8px;margin-top:8px;flex-wrap:wrap' });
  if (editable) acts.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Редактирай', 'aria-label': 'Редактирай версия ' + r.version, onClick: () => recipeForm(ctx, dish, arts, { existing: r }) }));
  if (r.status === 'draft') {
    acts.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Активирай', 'aria-label': 'Активирай версия ' + r.version, onClick: async () => {
      const ok = await confirmDialog('Да активирам ли версия ' + r.version + ' от ' + isoToBg(r.valid_from) + '? Досегашната активна версия остава за месеците преди тази дата.', { okText: 'Активирай' });
      if (!ok) return;
      try {
        await api('/recipes/' + r.id, { method: 'PUT', body: JSON.stringify(Object.assign(payload(r), { status: 'active' })) });
        toast('Версия ' + r.version + ' е активна.');
        ctx.reload();
      } catch (e) { toast(e.message, true); }
    } }));
    acts.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Изтрий', 'aria-label': 'Изтрий черновата, версия ' + r.version, onClick: async () => {
      const ok = await confirmDialog('Да изтрия ли черновата (версия ' + r.version + ')?', { okText: 'Изтрий', danger: true });
      if (!ok) return;
      try { await api('/recipes/' + r.id, { method: 'DELETE' }); toast('Черновата е изтрита.'); ctx.reload(); }
      catch (e) { toast(e.message, true); }
    } }));
  }
  if (acts.firstChild) box.appendChild(acts);
  return box;
}

// Полетата, които сървърът приема при запис (без изчислените за показване).
function payload(r) {
  return {
    company_id: r.company_id != null ? r.company_id : state().activeCompany,
    article_id: r.article_id, valid_from: r.valid_from, portion: r.portion, note: r.note || '',
    lines: (r.lines || []).map((l) => ({ article_id: l.article_id, qty: l.qty, unit: l.unit })),
  };
}

// Нова версия (opts.base — от коя да се копират редовете) или редакция
// (opts.existing — само неизползвана активна/чернова).
function recipeForm(ctx, dish, arts, opts) {
  const existing = opts.existing || null;
  const src = existing || opts.base || null;
  // Самото ястие не може да е в собствената си карта (сървърът хваща и
  // по-дългите цикли).
  const mats = arts.filter((a) => LINE_TYPES.includes(a.type) && a.active !== false && Number(a.id) !== Number(dish.id))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'bg'));
  const byId = {};
  arts.forEach((a) => { byId[a.id] = a; });
  const validFrom = dateInput(existing ? existing.valid_from : monthStart());
  const portion = input({ type: 'number', min: '1', step: '1', value: String((src && src.portion) || 1) });
  const note = input({ maxlength: '500', value: existing ? (existing.note || '') : '' });
  const asDraft = existing ? null : el('input', { type: 'checkbox' });
  const tb = el('tbody');
  // Търсачка вместо падащ списък (Макс, 21.10.2026: „като при фирмите — като
  // тръгна да пиша текста, да го разчита"): пише се част от името или кода.
  const matLabel = (a) => (a.code ? a.code + ' ' : '') + a.name + ' (' + (a.measure || '') + ')';
  const lines = [];
  const renumber = () => lines.forEach((l, i) => {
    l.cb.input.setAttribute('aria-label', 'Материал, ред ' + (i + 1));
    l.qty.setAttribute('aria-label', 'Количество, ред ' + (i + 1));
    l.unit.setAttribute('aria-label', 'Единица, ред ' + (i + 1));
    l.del.setAttribute('aria-label', 'Премахни ред ' + (i + 1));
  });
  function fillUnits(line, keep) {
    const a = byId[line.cb.getValue()];
    const us = unitsOf(a);
    if (keep && !us.includes(keep)) us.push(keep);
    clear(line.unit);
    us.forEach((u) => line.unit.appendChild(el('option', { value: u, text: u, selected: u === keep })));
    line.unit.disabled = !us.length;
  }
  function addLine(l) {
    const line = {};
    line.cb = combobox({
      items: mats, label: matLabel, search: (a) => (a.code || '') + ' ' + (a.name || ''),
      value: l && l.article_id != null ? l.article_id : null,
      placeholder: 'Пишете име или код на материала…', ariaLabel: 'Материал',
      emptyText: 'Няма такъв материал. Добавете го в „Артикули“ (тип Материал).',
      autoPickSingle: true,
      onPick: () => fillUnits(line, ''),
    });
    line.qty = input({ type: 'number', step: 'any', min: '0', value: l && l.qty != null ? String(l.qty) : '', style: 'width:110px' });
    line.unit = el('select', { style: 'width:110px' });
    line.del = el('button', { class: 'btn sm ghost', type: 'button', text: '×', onClick: () => {
      const i = lines.indexOf(line);
      if (i >= 0) lines.splice(i, 1);
      line.tr.remove();
      renumber();
    } });
    fillUnits(line, l && l.unit);
    line.tr = el('tr', {}, el('td', {}, line.cb), el('td', {}, line.qty), el('td', {}, line.unit), el('td', {}, line.del));
    lines.push(line);
    tb.appendChild(line.tr);
    renumber();
    return line;
  }
  ((src && src.lines) || []).forEach((l) => addLine(l));
  if (!lines.length) addLine(null);
  const body = el('div', {},
    existing ? null : (opts.base ? el('p', { class: 'cal-hint', text: 'Редовете са копирани от версия ' + opts.base.version + ' — променете каквото трябва. Старата версия остава за месеците преди новата дата.' }) : null),
    row(field('В сила от', validFrom, { size: 's' }), field('Количествата са за (порции)', portion, { size: 's' })),
    row(field('Бележка', note)),
    el('table', { class: 'list recipe-lines' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Материал' }), el('th', { text: 'Количество' }), el('th', { text: 'Единица' }), el('th', { text: '' }))),
      tb),
    el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави ред', onClick: () => { const l = addLine(null); l.cb.input.focus(); } }),
    asDraft ? el('label', { style: 'display:flex;gap:6px;align-items:center;margin-top:10px;cursor:pointer' }, asDraft, 'Запиши като чернова (не влиза в сила, докато не я активирате)') : null);
  const save = el('button', { class: 'btn', type: 'button', text: 'Запази', onClick: async () => {
    if (!validFrom.dataset.iso) { toast('Въведете дата „В сила от" (дд.мм.гггг).', true); validFrom.focus(); return; }
    const out = [];
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const artId = l.cb.getValue();
      if (artId == null && !l.qty.value.trim()) continue;
      if (artId == null) { toast('Изберете материал на ред ' + (i + 1) + ' — пишете името и изберете от списъка.', true); l.cb.input.focus(); return; }
      const q = Number(String(l.qty.value).replace(',', '.'));
      if (!(q > 0)) { toast('Количеството на ред ' + (i + 1) + ' трябва да е положително число.', true); l.qty.focus(); return; }
      out.push({ article_id: Number(artId), qty: q, unit: l.unit.value || '' });
    }
    if (!out.length) { toast('Добавете поне един материал.', true); return; }
    const body = {
      company_id: state().activeCompany, article_id: Number(dish.id), valid_from: validFrom.dataset.iso,
      portion: Number(portion.value) > 0 ? Number(portion.value) : 1, note: note.value.trim(), lines: out,
    };
    if (asDraft && asDraft.checked) body.status = 'draft';
    try {
      if (existing) await api('/recipes/' + existing.id, { method: 'PUT', body: JSON.stringify(body) });
      else await api('/recipes', { method: 'POST', body: JSON.stringify(body) });
      closeModal();
      toast(existing ? 'Рецептурата е записана.' : 'Новата версия е записана.');
      ctx.reload();
    } catch (e) {
      // 409 (използвана карта) и 400 (цикъл) идват с ясен текст от сървъра.
      toast(e.message, true);
    }
  } });
  openModal({ title: (existing ? 'Редакция на версия ' + existing.version : 'Нова версия') + ' — ' + dish.name, wide: true, body,
    foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), save) });
}

// ---- Внос от Excel ----

const REC_HINTS = {
  dish: ['ястие', 'рецепта', 'рецептура', 'dish', 'recipe', 'meal'],
  material: ['материал', 'суровина', 'съставка', 'продукт', 'material', 'ingredient', 'component'],
  qty: ['количество', 'к-во', 'кол.', 'qty', 'quantity', 'amount'],
  unit: ['мярка', 'единица', 'м.ед.', 'м. ед.', 'unit', 'uom'],
};

// Редове от Excel → { dish, material, qty, unit }. Празно „Ястие" = същото
// като горния ред: така обикновено се пишат картите на ръка (ястието веднъж,
// материалите под него).
export function recipeRowsFrom(rows) {
  const cols = [];
  (rows || []).slice(0, 50).forEach((r) => Object.keys(r).forEach((k) => { if (!cols.includes(k)) cols.push(k); }));
  const m = guessColumns(cols, REC_HINTS);
  const missing = ['dish', 'material', 'qty'].filter((k) => !m[k]);
  if (missing.length) return { error: 'Не открих колони за: ' + missing.map((k) => ({ dish: 'ястие', material: 'материал', qty: 'количество' })[k]).join(', ') + '. Очаквам заглавия като „Ястие", „Материал", „Количество", „Мярка".' };
  const out = [];
  let lastDish = '';
  for (const r of rows) {
    const s = (c) => (c ? String(r[c] == null ? '' : r[c]).trim() : '');
    const dish = s(m.dish) || lastDish;
    const material = s(m.material);
    const rawQty = s(m.qty);
    if (!material && !rawQty) { if (s(m.dish)) lastDish = s(m.dish); continue; }
    lastDish = dish;
    out.push({ dish, material, qty: Number(rawQty.replace(/[\s ]/g, '').replace(',', '.')), unit: s(m.unit) });
  }
  return { rows: out, mapping: m };
}

function importDialog(ctx) {
  const validFrom = dateInput(monthStart());
  const result = el('div', { 'aria-live': 'polite' });
  let parsed = null;
  const apply = el('button', { class: 'btn', type: 'button', text: 'Потвърди внос', disabled: 'disabled', onClick: async () => {
    if (!parsed) return;
    apply.disabled = true;
    try {
      const r = await api('/recipes/import', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, mode: 'apply', valid_from: validFrom.dataset.iso || undefined, rows: parsed }) });
      closeModal();
      const n = (r && r.created) ? r.created.length : 0;
      toast(plural(n, 'карта е създадена', 'карти са създадени') + (r && r.will_create_dishes ? ' и ' + plural(r.will_create_dishes, 'ново ястие', 'нови ястия') + ' в „Артикули"' : '') + '.');
      ctx.reload();
    } catch (e) { apply.disabled = false; toast(e.message, true); }
  } });
  const pick = el('button', { class: 'btn ghost', type: 'button', text: 'Избери файл…', onClick: () => {
    impReadFile(async (rows) => {
      clear(result);
      parsed = null;
      apply.disabled = true;
      const res = recipeRowsFrom(rows);
      if (res.error) { result.appendChild(el('p', { class: 'sev-warn', role: 'alert', text: res.error })); return; }
      if (!res.rows.length) { result.appendChild(el('p', { class: 'sev-warn', role: 'alert', text: 'Във файла няма редове с материал.' })); return; }
      let pv;
      try { pv = await api('/recipes/import', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, mode: 'preview', valid_from: validFrom.dataset.iso || undefined, rows: res.rows }) }); }
      catch (e) { result.appendChild(el('p', { class: 'sev-warn', role: 'alert', text: e.message })); return; }
      parsed = res.rows;
      renderPreview(result, pv);
      apply.disabled = !(pv && pv.will_create > 0);
    }, { accept: '.xlsx,.xls,.csv' });
  } });
  const body = el('div', {},
    el('p', { text: 'Файл с колони: Ястие; Материал; Количество; Мярка — по един ред на материал. Ястието и материалът се търсят по код или име. Ястие, което още го няма в „Артикули", се създава само. Празно „Ястие" значи същото като на горния ред.' }),
    row(field('В сила от', validFrom, { size: 's' })),
    el('div', { style: 'margin:6px 0 10px' }, pick),
    result);
  openModal({ title: 'Внос на рецептури от Excel', wide: true, body,
    foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), apply) });
}

function renderPreview(box, pv) {
  const cards = (pv && pv.cards) || [];
  const unmatched = (pv && pv.unmatched) || [];
  box.appendChild(el('p', { style: 'font-weight:700', text: 'Ще бъдат създадени ' + plural(pv.will_create || 0, 'карта', 'карти')
    + (pv.will_create_dishes ? ' и ' + plural(pv.will_create_dishes, 'ново ястие', 'нови ястия') : '')
    + '. ' + (unmatched.length ? plural(unmatched.length, 'ред спира', 'реда спират') + ' — причината е по-долу.' : '') }));
  if (cards.length) {
    box.appendChild(el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Ястие (файл)' }), el('th', { text: 'Намерено като' }), el('th', { class: 'num', text: 'Материали' }), el('th', { text: 'Версия' }), el('th', { text: '' }))),
      el('tbody', {}, ...cards.map((c) => el('tr', {},
        el('td', {}, c.dish || ''), el('td', {}, c.is_new ? el('span', { class: 'tag teal', text: 'ново ястие' }) : (c.name || '—')), el('td', { class: 'num' }, String(c.lines || 0)),
        el('td', {}, c.next_version ? 'v' + c.next_version : '—'),
        el('td', {}, c.ok ? el('span', { class: 'tag green', text: 'ще се създаде' }) : el('span', { class: 'tag red', text: 'спира — има проблемен ред' })))))));
  }
  if (unmatched.length) {
    box.appendChild(el('h3', { text: 'Редове, които спират' }));
    box.appendChild(el('p', { class: 'cal-hint', text: 'Карта с поне един такъв ред не се създава. Липсващ материал се добавя в „Артикули" (или се поправя името му във файла), непозната мярка се поправя във файла — и файлът се качва пак.' }));
    box.appendChild(el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, el('th', { class: 'num', text: 'Ред' }), el('th', { text: 'Ястие' }), el('th', { text: 'Материал' }), el('th', { text: 'Причина' }))),
      el('tbody', {}, ...unmatched.map((u) => el('tr', { class: 'stock-low' },
        el('td', { class: 'num' }, String(u.row || '')), el('td', {}, u.dish || ''), el('td', {}, u.material || ''), el('td', {}, u.reason || ''))))));
  }
}
